import { usePermission } from '@/core/permissions';
import type { SchoolClassDto } from '@/features/classes/types';
import { useMyMemberPublicId } from '@/stores/session';

/**
 * Does this member teach this class?
 *
 * `GET /api/classes/` returns every class of the school to teachers as well as
 * admins, so a payload row says nothing about whether the caller may act on it.
 * The class names its teachers by `public_id` and the session names the
 * caller's own Member id (backend ADR-016), so the answer is a local equality
 * check — no extra request, and no per-caller field on a cacheable payload.
 *
 * The server is still the authority (`IsAdminOrClassTeacher`); this only shapes
 * what we offer, so we never invite someone into a 403.
 */
export function isTaughtBy(cls: SchoolClassDto, memberPublicId: string): boolean {
  return cls.teachers.some(teacher => teacher.public_id === memberPublicId);
}

/**
 * The classes the caller may actually act on.
 *
 * Admins manage every class, so they get the list unchanged. A teacher gets
 * only their own — offering the rest produces a form that always fails on
 * submit, which is how the homework create dialog used to behave.
 */
export function useTaughtClasses(classes: SchoolClassDto[]): SchoolClassDto[] {
  const { isTeacher } = usePermission();
  const myMemberPublicId = useMyMemberPublicId();

  if (!isTeacher || !myMemberPublicId) return classes;
  return classes.filter(cls => isTaughtBy(cls, myMemberPublicId));
}
